import { useState, useEffect, useRef } from "react"
import { options } from "../util"
import { Stack, Typography, Box } from "@mui/material"
import { Field } from "formik"
import { FormContainer, NavButtons } from "../../../../Menu/Flow"
import employerFlowStyles from "../../styles"
import { styles } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields/util"

/**
 * Digits only, capped at 9. This is what gets stored in formik — no dash.
 */
function stripZip(value) {
    return String(value ?? "").replace(/\D/g, "").slice(0, 9)
}

/**
 * Display formatting only. "123456789" → "12345-6789", "12345" → "12345".
 * The dash is not added until a 6th digit arrives, so deleting back through
 * it behaves sensibly.
 */
function formatZip(value) {
    const digits = stripZip(value)
    if (digits.length > 5) {
        return `${digits.slice(0, 5)}-${digits.slice(5)}`
    }
    return digits
}

export default function Address(props) {
    const { mode, exitUrl, step, formik, googlePlaces, isSysAdmin } = props
    const { errors, setErrors, touched, setFieldValue, setFieldTouched } = formik

    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [inputValue, setInputValue] = useState(formik.values.addressLine1 || "")
    const placesServiceRef = useRef(null)

    const initPlacesService = () => {
        if (!placesServiceRef.current && window.google?.maps?.places?.PlacesService) {
            // Create a dummy div for PlacesService (required by Google Maps API)
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
    }

    const fetchAddressDetails = (placeId) => {
        return new Promise((resolve) => {
            initPlacesService();
            if (!placesServiceRef.current) {
                resolve(null);
                return;
            }

            placesServiceRef.current.getDetails(
                { placeId, fields: ['address_components', 'formatted_address'] },
                (place, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                        const addressComponents = place.address_components || [];
                        const addressDetails = {
                            formattedAddress: place.formatted_address,
                            streetNumber: '',
                            route: '',
                            city: '',
                            state: '',
                            zip: '',
                            country: ''
                        };
                        addressComponents.forEach(component => {
                            const types = component.types;
                            if (types.includes('street_number')) {
                                addressDetails.streetNumber = component.long_name;
                            } else if (types.includes('route')) {
                                addressDetails.route = component.long_name;
                            } else if (types.includes('locality') || types.includes("sublocality")) {
                                addressDetails.city = component.long_name;
                            } else if (types.includes('administrative_area_level_1')) {
                                addressDetails.state = component.short_name;
                            } else if (types.includes('postal_code')) {
                                addressDetails.zip = component.long_name;
                            } else if (types.includes('country')) {
                                addressDetails.country = component.short_name;
                            }
                        });

                        resolve(addressDetails);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    };

    const handleAddressSearch = async (value) => {
        setInputValue(value)
        // setFieldValue("addressLine1", value)

        if (!value || value.length < 3) {
            setSuggestions([])
            setShowSuggestions(false)
            return
        }

        try {
            const places = await googlePlaces?.getPlacePredictions({
                input: value,
                types: ["address"]
            })

            if (places?.predictions) {
                setSuggestions(places.predictions)
                setShowSuggestions(true)
            } else {
                setSuggestions([])
                setShowSuggestions(false)
            }
            setFieldValue("addressLine1", value, false) // false = don't validate
        } catch (error) {
            console.error("Error getting place predictions:", error)
        }
    }

    const handleAddressSelect = async (suggestion) => {
        setShowSuggestions(false)

        // Use Places API getDetails with place_id (preferred method)
        try {
            const addressDetails = await fetchAddressDetails(suggestion.place_id)
            if (addressDetails) {
                // Combine street number and route for addressLine1
                const addressLine1 = [addressDetails.streetNumber, addressDetails.route]
                    .filter(Boolean)
                    .join(" ")
                    .trim()

                // Update input value to show only the street address, not the full description
                setInputValue(addressLine1)

                // Set field values without validation
                setFieldValue("addressLine1", addressLine1, false) // false = don't validate
                setFieldValue("city", addressDetails.city, false)
                setFieldValue("state", addressDetails.state, false)
                // Google can return zip+4 already dashed — store digits only.
                setFieldValue("zip", stripZip(addressDetails.zip), false)

                // Clear validation errors for address fields
                const updatedErrors = { ...errors }
                delete updatedErrors.addressLine1
                delete updatedErrors.city
                delete updatedErrors.state
                delete updatedErrors.zip
                setErrors(updatedErrors)

                // Reset touched state for address fields to prevent showing errors
                setFieldTouched("addressLine1", false, false)
                setFieldTouched("city", false, false)
                setFieldTouched("state", false, false)
                setFieldTouched("zip", false, false)
            }
        } catch (error) {
            console.error("Error fetching address details:", error)
        }
    }

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setShowSuggestions(false)
        if (showSuggestions) {
            document.addEventListener('click', handleClickOutside)
        }
        return () => document.removeEventListener('click', handleClickOutside)
    }, [showSuggestions])

    return (
        <Stack spacing="30px" height="100%">
            <FormContainer>
                <Typography variant="h2" sx={employerFlowStyles.h2}>Address</Typography>
                <Typography>Enter your official place of business.</Typography>

                <Stack spacing={2} sx={{ marginTop: "40px" }}>
                    {/* Address Line 1 with Autocomplete */}
                    <Stack sx={{ position: 'relative' }}>
                        <Typography sx={{ marginBottom: "8px" }}>
                            Company Street Address
                        </Typography>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => handleAddressSearch(e.target.value)}
                            name="addressLine1"
                            placeholder="Start typing address..."
                            style={{
                                width: "100%",
                                padding: "12px",
                                border: `1px solid ${errors.addressLine1 && touched.addressLine1 ? "#dc2626" : "#ACBEE8"}`,
                                borderRadius: "8px",
                                fontSize: "14px"
                            }}
                        />

                        {/* Suggestions Dropdown */}
                        {showSuggestions && suggestions.length > 0 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    backgroundColor: 'white',
                                    border: '1px solid #ACBEE8',
                                    borderRadius: '8px',
                                    marginTop: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                }}
                            >
                                {suggestions.map((suggestion, index) => (
                                    <Box
                                        key={suggestion.place_id || index}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleAddressSelect(suggestion)
                                        }}
                                        sx={{
                                            padding: '12px',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#f3f4f6'
                                            },
                                            borderBottom: index < suggestions.length - 1 ? '1px solid #e5e7eb' : 'none'
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {suggestion.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {errors.addressLine1 && touched.addressLine1 && (
                            <div className="error-message-fields">{errors.addressLine1}</div>
                        )}
                    </Stack>

                    {/* Floor/Suite and City Row */}
                    <Stack direction="row" spacing={2}>
                        <Stack flex={1}>
                            <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                                Floor, Suite, Building, etc. (optional)
                            </Typography>
                            <Field name="addressLine2">
                                {({ field }) => {
                                    const { onBlur, ...fieldProps } = field;
                                    return (
                                        <input
                                            {...fieldProps}
                                            type="text"
                                            placeholder="Optional"
                                            style={{
                                                width: "100%",
                                                padding: "12px",
                                                border: "1px solid #ACBEE8",
                                                borderRadius: "8px",
                                                fontSize: "14px"
                                            }}
                                        />
                                    );
                                }}
                            </Field>
                        </Stack>

                        <Stack flex={1}>
                            <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                                City
                            </Typography>
                            <Field name="city">
                                {({ field }) => {
                                    const { onBlur, ...fieldProps } = field;
                                    return (
                                        <input
                                            {...fieldProps}
                                            type="text"
                                            style={{
                                                width: "100%",
                                                padding: "12px",
                                                border: `1px solid ${errors.city && touched.city ? "#dc2626" : "#ACBEE8"}`,
                                                borderRadius: "8px",
                                                fontSize: "14px"
                                            }}
                                        />
                                    );
                                }}
                            </Field>
                            {errors.city && touched.city && (
                                <div className="error-message-fields">{errors.city}</div>
                            )}
                        </Stack>
                    </Stack>

                    {/* State and Zip Code Row */}
                    <Stack direction="row" spacing={2}>
                        <Stack flex={1}>
                            <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                                State
                            </Typography>
                            <Field name="state">
                                {({ field }) => {
                                    const { onBlur, ...fieldProps } = field;
                                    return (
                                        <select
                                            {...fieldProps}
                                            style={{...styles.select(!fieldProps.value), border: `1px solid ${errors.state && touched.state ? "#dc2626" : "#ACBEE8"}`}}
                                        >
                                            <option value="" >Select State</option>
                                            {Object.entries(options.stateArray).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </select>
                                    );
                                }}
                            </Field>
                            {errors.state && touched.state && (
                                <div className="error-message-fields">{errors.state}</div>
                            )}
                        </Stack>

                        <Stack flex={1}>
                            <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                                Zip Code
                            </Typography>
                            <Field name="zip">
                                {({ field }) => {
                                    // onChange is pulled out of the spread alongside onBlur so
                                    // formik's handler doesn't overwrite ours below.
                                    const { onBlur, onChange, ...fieldProps } = field;
                                    return (
                                        <input
                                            {...fieldProps}
                                            type="text"
                                            inputMode="numeric"
                                            // 10 to allow for the displayed dash.
                                            maxLength="10"
                                            // Display is dashed; formik stores digits only.
                                            value={formatZip(fieldProps.value)}
                                            onChange={(e) =>
                                                setFieldValue("zip", stripZip(e.target.value), false)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "12px",
                                                border: `1px solid ${errors.zip && touched.zip ? "#dc2626" : "#ACBEE8"}`,
                                                borderRadius: "8px",
                                                fontSize: "14px"
                                            }}
                                        />
                                    );
                                }}
                            </Field>
                            {errors.zip && touched.zip && (
                                <div className="error-message-fields">{errors.zip}</div>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </FormContainer>

            <NavButtons mode={mode} enableSave={true} exitUrl={exitUrl} step={step} isSysAdmin={isSysAdmin} />
        </Stack>
    )
}
